import React, { useEffect, useState } from "react";
import "./Inwards.css";

const Inwards = () => {
    const [inwards, setInwards] = useState([]);
    const [vendors, setVendors] = useState([]);

    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteCode, setDeleteCode] = useState("");
    const [deleteInwardId, setDeleteInwardId] = useState(null);
    const [sendingDeleteCode, setSendingDeleteCode] = useState(false);
    const [deletingInward, setDeletingInward] = useState(false);

    // OTP recipient email
    const [otpRecipientEmail, setOtpRecipientEmail] = useState("");

    const [formData, setFormData] = useState({
        vendor_id: "",
        category_code: "",
        craft_code: "",
        item_name: "",
        date: "",
        quantity: "",
        image: null,
    });

    const [imagePreview, setImagePreview] = useState(null);

    const [categories, setCategories] = useState([]);
    const [craftTypes, setCraftTypes] = useState([]);

    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showCraftModal, setShowCraftModal] = useState(false);

    const [newCategory, setNewCategory] = useState({
        category_name: "",
        category_code: "",
    });

    const [newCraft, setNewCraft] = useState({
        craft_name: "",
        craft_code: "",
    });

    const [savingCategory, setSavingCategory] = useState(false);
    const [savingCraft, setSavingCraft] = useState(false);

    // ============================================================
    // FETCH INWARDS
    // ============================================================

    const fetchInwards = async () => {
        try {
            setLoading(true);

            const response = await fetch("/inwards");

            if (!response.ok) {
                throw new Error("Failed to fetch inwards");
            }

            const data = await response.json();

            setInwards(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching inwards:", error);
            alert("Failed to load inward records.");
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // FETCH VENDORS
    // ============================================================

    const fetchVendors = async () => {
        try {
            const response = await fetch("/vendors");

            if (!response.ok) {
                throw new Error("Failed to fetch vendors");
            }

            const data = await response.json();

            setVendors(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching vendors:", error);
        }
    };

    // ============================================================
    // FETCH CATEGORIES
    // ============================================================

    const fetchCategories = async () => {
        try {
            const response = await fetch("/product-categories");

            if (!response.ok) {
                throw new Error("Failed to fetch categories");
            }

            const data = await response.json();

            setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    // ============================================================
    // FETCH CRAFT TYPES
    // ============================================================

    const fetchCraftTypes = async () => {
        try {
            const response = await fetch("/craft-types");

            if (!response.ok) {
                throw new Error("Failed to fetch craft types");
            }

            const data = await response.json();

            setCraftTypes(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching craft types:", error);
        }
    };

    // ============================================================
    // INITIAL LOAD
    // ============================================================

    useEffect(() => {
        fetchInwards();
        fetchVendors();
        fetchCategories();
        fetchCraftTypes();
    }, []);

    // ============================================================
    // FORM INPUT
    // ============================================================

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "image") {
            const file =
                files && files.length > 0
                    ? files[0]
                    : null;

            setFormData((prev) => ({
                ...prev,
                image: file,
            }));

            if (file) {
                setImagePreview(
                    URL.createObjectURL(file)
                );
            } else {
                setImagePreview(null);
            }

            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // ============================================================
    // RESET FORM
    // ============================================================

    const resetForm = () => {
        setFormData({
            vendor_id: "",
            category_code: "",
            craft_code: "",
            item_name: "",
            date: "",
            quantity: "",
            image: null,
        });

        setImagePreview(null);

        const imageInput =
            document.getElementById("inward-image");

        if (imageInput) {
            imageInput.value = "";
        }
    };

    // ============================================================
    // ADD INWARD
    // ============================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.vendor_id) {
            alert("Please select a vendor.");
            return;
        }

        if (!formData.category_code) {
            alert("Please select a category.");
            return;
        }

        if (!formData.craft_code) {
            alert("Please select a craft type.");
            return;
        }

        if (!formData.item_name.trim()) {
            alert("Please enter item name.");
            return;
        }

        if (!formData.date) {
            alert("Please select date.");
            return;
        }

        if (
            !formData.quantity ||
            Number(formData.quantity) <= 0
        ) {
            alert("Please enter a valid quantity.");
            return;
        }

        if (!formData.image) {
            alert("Please upload product image.");
            return;
        }

        try {
            const data = new FormData();

            data.append(
                "vendor_id",
                formData.vendor_id
            );

            data.append(
                "category_code",
                formData.category_code
            );

            data.append(
                "craft_code",
                formData.craft_code
            );

            data.append(
                "item_name",
                formData.item_name
            );

            data.append(
                "date",
                formData.date
            );

            data.append(
                "quantity",
                formData.quantity
            );

            data.append(
                "image",
                formData.image
            );

            const response = await fetch(
                "/inwards",
                {
                    method: "POST",
                    body: data,
                }
            );

            const result =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    result.error ||
                    result.message ||
                    "Failed to add inward"
                );
            }

            alert(
                "Inward added successfully."
            );

            resetForm();

            setShowForm(false);

            await fetchInwards();

        } catch (error) {

            console.error(
                "Error adding inward:",
                error
            );

            alert(
                error.message ||
                "Failed to add inward."
            );
        }
    };

    // ============================================================
    // SEND INWARD DELETE OTP
    // ============================================================

    const deleteInwards = async (row) => {

        try {

            const inwardId =
                row.inward_id;

            const vendorId =
                String(
                    row.vendor_id || ""
                ).trim();


            // ----------------------------------------------------
            // VALIDATE INWARD ID
            // ----------------------------------------------------

            if (!inwardId) {

                alert(
                    "Invalid inward ID."
                );

                return;
            }


            // ----------------------------------------------------
            // VALIDATE VENDOR ID
            // ----------------------------------------------------

            if (!vendorId) {

                alert(
                    "Vendor ID is missing for this inward record."
                );

                return;
            }


            // ----------------------------------------------------
            // FIND VENDOR
            // ----------------------------------------------------

            const vendor =
                vendors.find(
                    (item) =>
                        String(
                            item.vendor_id || ""
                        ).trim() ===
                        vendorId
                );


            // ----------------------------------------------------
            // CHECK EMAIL
            // ----------------------------------------------------

            if (
                vendor &&
                !vendor.email
            ) {

                alert(
                    "This vendor does not have a registered email address. Please update the vendor details first."
                );

                return;
            }


            // ----------------------------------------------------
            // CONFIRM DELETE
            // ----------------------------------------------------

            const confirmDelete =
                window.confirm(
                    "Are you sure you want to delete this inward entry?\n\nA 6-digit verification code will be sent to the vendor's registered email."
                );


            if (!confirmDelete) {
                return;
            }


            setSendingDeleteCode(true);


            // ====================================================
            // SEND INWARD-SPECIFIC OTP
            // ====================================================

            const sendResponse =
                await fetch(
                    "/send-inward-delete-code",
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body: JSON.stringify({
                            inward_id:
                                inwardId,
                        }),
                    }
                );


            const sendResult =
                await sendResponse.json();


            if (!sendResponse.ok) {

                throw new Error(
                    sendResult.error ||
                    sendResult.message ||
                    "Could not send the verification code."
                );
            }


            // ====================================================
            // SAVE SELECTED INWARD
            // ====================================================

            setDeleteInwardId(
                inwardId
            );


            // ====================================================
            // SAVE RECIPIENT EMAIL
            // ====================================================

            setOtpRecipientEmail(
                sendResult.email ||
                sendResult.masked_email ||
                (vendor
                    ? vendor.email
                    : "")
            );


            setDeleteCode("");

            setShowDeleteModal(true);


            alert(
                "Verification code sent to the vendor's registered email."
            );


        } catch (error) {

            console.error(
                "Error sending delete code:",
                error
            );


            alert(
                error.message ||
                "Could not send the verification code."
            );


        } finally {

            setSendingDeleteCode(
                false
            );
        }
    };

    // ============================================================
    // VERIFY OTP AND DELETE INWARD
    // ============================================================

    const verifyDeleteCode = async () => {

        const enteredCode =
            String(
                deleteCode || ""
            ).trim();


        // --------------------------------------------------------
        // VALIDATE OTP
        // --------------------------------------------------------

        if (
            !/^\d{6}$/.test(
                enteredCode
            )
        ) {

            alert(
                "Please enter the 6-digit verification code."
            );

            return;
        }


        // --------------------------------------------------------
        // CHECK INWARD ID
        // --------------------------------------------------------

        if (!deleteInwardId) {

            alert(
                "Invalid inward record."
            );

            return;
        }


        try {

            setDeletingInward(true);


            // ====================================================
            // FIND SELECTED INWARD
            // ====================================================

            const selectedInward =
                inwards.find(
                    (item) =>
                        String(
                            item.inward_id
                        ) ===
                        String(
                            deleteInwardId
                        )
                );


            if (!selectedInward) {

                alert(
                    "Inward record not found."
                );

                return;
            }


            // ====================================================
            // GET VENDOR ID
            // ====================================================

            const vendorId =
                String(
                    selectedInward.vendor_id ||
                    ""
                ).trim();


            if (!vendorId) {

                alert(
                    "Vendor ID is missing."
                );

                return;
            }


            // ====================================================
            // VERIFY OTP + DELETE INWARD
            // ====================================================

            const response =
                await fetch(
                    `/inwards/${deleteInwardId}`,
                    {
                        method: "DELETE",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body: JSON.stringify({
                            vendor_id:
                                vendorId,

                            code:
                                enteredCode,
                        }),
                    }
                );
const responseText = await response.text();

console.log("DELETE STATUS:", response.status);
console.log("DELETE RESPONSE:", responseText);

let result;

try {
    result = JSON.parse(responseText);
} catch (parseError) {
    console.error("Server returned non-JSON response:", responseText);

    throw new Error(
        "Server returned an HTML page instead of JSON. Check the Flask DELETE route or Apache configuration."
    );
}

            
            // ====================================================
            // HANDLE ERROR
            // ====================================================

            if (!response.ok) {

                throw new Error(
                    result.error ||
                    result.message ||
                    "Invalid verification code."
                );
            }


            // ====================================================
            // SUCCESS
            // ====================================================

            alert(
                "Inward deleted successfully."
            );


            // ----------------------------------------------------
            // RESET OTP MODAL
            // ----------------------------------------------------

            setShowDeleteModal(
                false
            );

            setDeleteCode("");

            setDeleteInwardId(
                null
            );

            setOtpRecipientEmail("");


            // ----------------------------------------------------
            // REFRESH TABLE
            // ----------------------------------------------------

            await fetchInwards();


        } catch (error) {

            console.error(
                "Error deleting inward:",
                error
            );


            alert(
                error.message ||
                "Failed to delete inward entry."
            );


        } finally {

            setDeletingInward(
                false
            );
        }
    };

    // ============================================================
    // ADD CATEGORY
    // ============================================================

    const handleAddCategory = async () => {

        const categoryName =
            newCategory.category_name.trim();

        const categoryCode =
            newCategory.category_code
                .trim()
                .toUpperCase();


        if (!categoryName) {

            alert(
                "Please enter category name."
            );

            return;
        }


        if (
            !/^[A-Z]{3}$/.test(
                categoryCode
            )
        ) {

            alert(
                "Category code must contain exactly 3 uppercase letters."
            );

            return;
        }


        try {

            setSavingCategory(
                true
            );


            const response =
                await fetch(
                    "/product-categories",
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body: JSON.stringify({
                            category_name:
                                categoryName,

                            category_code:
                                categoryCode,
                        }),
                    }
                );


            const result =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    result.error ||
                    result.message ||
                    "Failed to add category."
                );
            }


            alert(
                "Category added successfully."
            );


            setNewCategory({
                category_name: "",
                category_code: "",
            });


            setShowCategoryModal(
                false
            );


            await fetchCategories();


        } catch (error) {

            console.error(
                "Error adding category:",
                error
            );


            alert(
                error.message ||
                "Failed to add category."
            );


        } finally {

            setSavingCategory(
                false
            );
        }
    };

    // ============================================================
    // DELETE CATEGORY
    // ============================================================

    const handleDeleteCategory =
        async (category) => {

            const code =
                category.category_code;


            if (
                !window.confirm(
                    `Are you sure you want to delete category "${category.category_name}"?`
                )
            ) {

                return;
            }


            try {

                const response =
                    await fetch(
                        `/product-categories/${encodeURIComponent(
                            code
                        )}`,
                        {
                            method: "DELETE",
                        }
                    );


                const result =
                    await response.json();


                if (!response.ok) {

                    throw new Error(
                        result.error ||
                        result.message ||
                        "Failed to delete category."
                    );
                }


                alert(
                    "Category deleted successfully."
                );


                await fetchCategories();


            } catch (error) {

                console.error(
                    "Error deleting category:",
                    error
                );


                alert(
                    error.message ||
                    "Failed to delete category."
                );
            }
        };

    // ============================================================
    // ADD CRAFT
    // ============================================================

    const handleAddCraft = async () => {

        const craftName =
            newCraft.craft_name.trim();

        const craftCode =
            newCraft.craft_code
                .trim()
                .toUpperCase();


        if (!craftName) {

            alert(
                "Please enter craft name."
            );

            return;
        }


        if (
            !/^[A-Z]{3}$/.test(
                craftCode
            )
        ) {

            alert(
                "Craft code must contain exactly 3 uppercase letters."
            );

            return;
        }


        try {

            setSavingCraft(
                true
            );


            const response =
                await fetch(
                    "/craft-types",
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body: JSON.stringify({
                            craft_name:
                                craftName,

                            craft_code:
                                craftCode,
                        }),
                    }
                );


            const result =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    result.error ||
                    result.message ||
                    "Failed to add craft type."
                );
            }


            alert(
                "Craft type added successfully."
            );


            setNewCraft({
                craft_name: "",
                craft_code: "",
            });


            setShowCraftModal(
                false
            );


            await fetchCraftTypes();


        } catch (error) {

            console.error(
                "Error adding craft:",
                error
            );


            alert(
                error.message ||
                "Failed to add craft type."
            );


        } finally {

            setSavingCraft(
                false
            );
        }
    };

    // ============================================================
    // DELETE CRAFT
    // ============================================================

    const handleDeleteCraft =
        async (craft) => {

            const code =
                craft.craft_code;


            if (
                !window.confirm(
                    `Are you sure you want to delete craft "${craft.craft_name}"?`
                )
            ) {

                return;
            }


            try {

                const response =
                    await fetch(
                        `/craft-types/${encodeURIComponent(
                            code
                        )}`,
                        {
                            method: "DELETE",
                        }
                    );


                const result =
                    await response.json();


                if (!response.ok) {

                    throw new Error(
                        result.error ||
                        result.message ||
                        "Failed to delete craft type."
                    );
                }


                alert(
                    "Craft type deleted successfully."
                );


                await fetchCraftTypes();


            } catch (error) {

                console.error(
                    "Error deleting craft:",
                    error
                );


                alert(
                    error.message ||
                    "Failed to delete craft type."
                );
            }
        };

    // ============================================================
    // FORMAT DATE
    // ============================================================

    const formatDate = (date) => {

        if (!date) {
            return "";
        }


        try {

            const d =
                new Date(date);


            if (
                Number.isNaN(
                    d.getTime()
                )
            ) {

                return date;
            }


            return d.toLocaleDateString(
                "en-GB"
            );


        } catch {

            return date;
        }
    };

// ============================================================
// OPEN PRODUCT IMAGE
// ============================================================

const openImage = (imagePath) => {
    if (!imagePath) {
        return;
    }

    window.open(
        imagePath,
        "_blank",
        "noopener,noreferrer"
    );
};

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="inwards-container">

            {/* =====================================================
                HEADER
            ====================================================== */}

            <div className="inwards-header">

                <div>

                    <h2>
                        Inwards
                    </h2>

                    <p>
                        Manage incoming products and inventory.
                    </p>

                </div>


                <button
                    className="add-inward-btn"
                    onClick={() =>
                        setShowForm(!showForm)
                    }
                >

                    {showForm
                        ? "Close"
                        : "+ Add Inward"}

                </button>

            </div>


            {/* =====================================================
                ADD INWARD FORM
            ====================================================== */}

            {showForm && (

                <div className="inward-form-container">

                    <form
                        onSubmit={
                            handleSubmit
                        }
                    >

                        {/* =================================================
                            ROW 1
                        ================================================= */}

                        <div className="form-row">

                            {/* Vendor */}

                            <div className="form-group">

                                <label>
                                    Vendor
                                </label>


                                <select
                                    name="vendor_id"
                                    value={
                                        formData.vendor_id
                                    }
                                    onChange={
                                        handleChange
                                    }
                                >

                                    <option value="">
                                        Select Vendor
                                    </option>


                                    {vendors.map(
                                        (vendor) => (

                                            <option
                                                key={
                                                    vendor.vendor_id
                                                }
                                                value={
                                                    vendor.vendor_id
                                                }
                                            >

                                                {
                                                    vendor.vendor_name
                                                }

                                                {" - "}

                                                {
                                                    vendor.vendor_id
                                                }

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>


                            {/* Category */}

                            <div className="form-group">

                                <label>
                                    Category
                                </label>


                                <div className="select-with-button">

                                    <select
                                        name="category_code"
                                        value={
                                            formData.category_code
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    >

                                        <option value="">
                                            Select Category
                                        </option>


                                        {categories.map(
                                            (category) => (

                                                <option
                                                    key={
                                                        category.category_code
                                                    }
                                                    value={
                                                        category.category_code
                                                    }
                                                >

                                                    {
                                                        category.category_name
                                                    }

                                                    {" ("}

                                                    {
                                                        category.category_code
                                                    }

                                                    {")"}

                                                </option>

                                            )
                                        )}

                                    </select>


                                    <button
                                        type="button"
                                        className="small-add-btn"
                                        onClick={() =>
                                            setShowCategoryModal(
                                                true
                                            )
                                        }
                                    >

                                        + Add

                                    </button>

                                </div>

                            </div>

                        </div>


                        {/* =================================================
                            ROW 2
                        ================================================= */}

                        <div className="form-row">

                            {/* Craft */}

                            <div className="form-group">

                                <label>
                                    Craft Type
                                </label>


                                <div className="select-with-button">

                                    <select
                                        name="craft_code"
                                        value={
                                            formData.craft_code
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    >

                                        <option value="">
                                            Select Craft Type
                                        </option>


                                        {craftTypes.map(
                                            (craft) => (

                                                <option
                                                    key={
                                                        craft.craft_code
                                                    }
                                                    value={
                                                        craft.craft_code
                                                    }
                                                >

                                                    {
                                                        craft.craft_name
                                                    }

                                                    {" ("}

                                                    {
                                                        craft.craft_code
                                                    }

                                                    {")"}

                                                </option>

                                            )
                                        )}

                                    </select>


                                    <button
                                        type="button"
                                        className="small-add-btn"
                                        onClick={() =>
                                            setShowCraftModal(
                                                true
                                            )
                                        }
                                    >

                                        + Add

                                    </button>

                                </div>

                            </div>


                            {/* Item Name */}

                            <div className="form-group">

                                <label>
                                    Item Name
                                </label>


                                <input
                                    type="text"
                                    name="item_name"
                                    value={
                                        formData.item_name
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter item name"
                                />

                            </div>

                        </div>


                        {/* =================================================
                            ROW 3
                        ================================================= */}

                        <div className="form-row">

                            {/* Date */}

                            <div className="form-group">

                                <label>
                                    Date
                                </label>


                                <input
                                    type="date"
                                    name="date"
                                    value={
                                        formData.date
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* Quantity */}

                            <div className="form-group">

                                <label>
                                    Quantity
                                </label>


                                <input
                                    type="number"
                                    name="quantity"
                                    min="1"
                                    value={
                                        formData.quantity
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter quantity"
                                />

                            </div>

                        </div>


                        {/* =================================================
                            IMAGE
                        ================================================= */}

                        <div className="form-group">

                            <label>
                                Product Image
                            </label>


                            <input
                                id="inward-image"
                                type="file"
                                name="image"
                                accept="image/*"
                                onChange={
                                    handleChange
                                }
                            />


                            {imagePreview && (

                                <div className="image-preview">

                                    <img
                                        src={
                                            imagePreview
                                        }
                                        alt="Product preview"
                                    />

                                </div>

                            )}

                        </div>


                        {/* =================================================
                            FORM BUTTONS
                        ================================================= */}

                        <div className="form-actions">

                            <button
                                type="submit"
                                className="save-btn"
                            >
                                Save Inward
                            </button>


                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => {

                                    resetForm();

                                    setShowForm(
                                        false
                                    );

                                }}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </div>

            )}


            {/* =====================================================
                INWARDS TABLE
            ====================================================== */}

            <div className="inwards-table-container">

                {loading ? (

                    <div className="loading">
                        Loading...
                    </div>

                ) : inwards.length === 0 ? (

                    <div className="no-data">
                        No inward records found.
                    </div>

                ) : (

                    <table className="inwards-table">

                        <thead>

                            <tr>

                                <th>
                                    Inward ID
                                </th>

                                <th>
                                    SKU
                                </th>

                                <th>
                                    Vendor
                                </th>

                                <th>
                                    Category
                                </th>

                                <th>
                                    Craft
                                </th>

                                <th>
                                    Item Name
                                </th>

                                <th>
                                    Date
                                </th>

                                <th>
                                    Quantity
                                </th>

                                <th>
                                    Image
                                </th>

                                <th>
                                    Action
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {inwards.map(
                                (row) => (

                                    <tr
                                        key={
                                            row.inward_id
                                        }
                                    >

                                        <td>
                                            {
                                                row.inward_id
                                            }
                                        </td>


                                        <td>
                                            {
                                                row.sku
                                            }
                                        </td>


                                        <td>
                                            {
                                                row.vendor_name ||
                                                row.vendor_id
                                            }
                                        </td>


                                        <td>
                                            {
                                                row.category_name ||
                                                row.category_code
                                            }
                                        </td>


                                        <td>
                                            {
                                                row.craft_name ||
                                                row.craft_code
                                            }
                                        </td>


                                        <td>
                                            {
                                                row.item_name
                                            }
                                        </td>


                                        <td>
                                            {
                                                formatDate(
                                                    row.date
                                                )
                                            }
                                        </td>


                                        <td>
                                            {
                                                row.quantity
                                            }
                                        </td>


                                    <td className="image-cell">

    {row.image_path ? (

        <img
            src={row.image_path}
            alt={row.item_name || "Product"}
            className="inward-product-image"
            onClick={() =>
                openImage(row.image_path)
            }
            title="Click to view image"
        />

    ) : (

        <span className="no-image">
            No Image
        </span>

    )}

</td>

                                        <td>

                                            <button
                                                type="button"
                                                className="delete-btn"
                                                onClick={() =>
                                                    deleteInwards(
                                                        row
                                                    )
                                                }
                                                disabled={
                                                    sendingDeleteCode
                                                }
                                            >

                                                Delete

                                            </button>

                                        </td>

                                    </tr>

                                )
                            )}

                        </tbody>

                    </table>

                )}

            </div>


            {/* =====================================================
                CATEGORY MODAL
            ====================================================== */}

            {showCategoryModal && (

                <div className="modal-overlay">

                    <div className="modal-content">

                        <h3>
                            Add Product Category
                        </h3>


                        <div className="form-group">

                            <label>
                                Category Name
                            </label>


                            <input
                                type="text"
                                value={
                                    newCategory.category_name
                                }
                                onChange={(e) =>
                                    setNewCategory({
                                        ...newCategory,
                                        category_name:
                                            e.target.value,
                                    })
                                }
                                placeholder="Example: Jewellery"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Category Code
                            </label>


                            <input
                                type="text"
                                maxLength="3"
                                value={
                                    newCategory.category_code
                                }
                                onChange={(e) =>
                                    setNewCategory({
                                        ...newCategory,
                                        category_code:
                                            e.target.value
                                                .toUpperCase()
                                                .replace(
                                                    /[^A-Z]/g,
                                                    ""
                                                ),
                                    })
                                }
                                placeholder="Example: JEW"
                            />

                        </div>


                        <div className="modal-actions">

                            <button
                                type="button"
                                className="save-btn"
                                onClick={
                                    handleAddCategory
                                }
                                disabled={
                                    savingCategory
                                }
                            >

                                {
                                    savingCategory
                                        ? "Saving..."
                                        : "Save"
                                }

                            </button>


                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => {

                                    setShowCategoryModal(
                                        false
                                    );

                                    setNewCategory({
                                        category_name: "",
                                        category_code: "",
                                    });

                                }}
                            >

                                Cancel

                            </button>

                        </div>


                        {/* Existing Categories */}

                        {categories.length > 0 && (

                            <div className="existing-items">

                                <h4>
                                    Existing Categories
                                </h4>


                                {categories.map(
                                    (category) => (

                                        <div
                                            className="existing-item"
                                            key={
                                                category.category_code
                                            }
                                        >

                                            <span>

                                                {
                                                    category.category_name
                                                }

                                                {" ("}

                                                {
                                                    category.category_code
                                                }

                                                {")"}

                                            </span>


                                            <button
                                                type="button"
                                                className="delete-small-btn"
                                                onClick={() =>
                                                    handleDeleteCategory(
                                                        category
                                                    )
                                                }
                                            >

                                                Delete

                                            </button>

                                        </div>

                                    )
                                )}

                            </div>

                        )}

                    </div>

                </div>

            )}


            {/* =====================================================
                CRAFT MODAL
            ====================================================== */}

            {showCraftModal && (

                <div className="modal-overlay">

                    <div className="modal-content">

                        <h3>
                            Add Craft Type
                        </h3>


                        <div className="form-group">

                            <label>
                                Craft Name
                            </label>


                            <input
                                type="text"
                                value={
                                    newCraft.craft_name
                                }
                                onChange={(e) =>
                                    setNewCraft({
                                        ...newCraft,
                                        craft_name:
                                            e.target.value,
                                    })
                                }
                                placeholder="Example: Crochet"
                            />

                        </div>


                        <div className="form-group">

                            <label>
                                Craft Code
                            </label>


                            <input
                                type="text"
                                maxLength="3"
                                value={
                                    newCraft.craft_code
                                }
                                onChange={(e) =>
                                    setNewCraft({
                                        ...newCraft,
                                        craft_code:
                                            e.target.value
                                                .toUpperCase()
                                                .replace(
                                                    /[^A-Z]/g,
                                                    ""
                                                ),
                                    })
                                }
                                placeholder="Example: CRO"
                            />

                        </div>


                        <div className="modal-actions">

                            <button
                                type="button"
                                className="save-btn"
                                onClick={
                                    handleAddCraft
                                }
                                disabled={
                                    savingCraft
                                }
                            >

                                {
                                    savingCraft
                                        ? "Saving..."
                                        : "Save"
                                }

                            </button>


                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => {

                                    setShowCraftModal(
                                        false
                                    );

                                    setNewCraft({
                                        craft_name: "",
                                        craft_code: "",
                                    });

                                }}
                            >

                                Cancel

                            </button>

                        </div>


                        {/* Existing Crafts */}

                        {craftTypes.length > 0 && (

                            <div className="existing-items">

                                <h4>
                                    Existing Craft Types
                                </h4>


                                {craftTypes.map(
                                    (craft) => (

                                        <div
                                            className="existing-item"
                                            key={
                                                craft.craft_code
                                            }
                                        >

                                            <span>

                                                {
                                                    craft.craft_name
                                                }

                                                {" ("}

                                                {
                                                    craft.craft_code
                                                }

                                                {")"}

                                            </span>


                                            <button
                                                type="button"
                                                className="delete-small-btn"
                                                onClick={() =>
                                                    handleDeleteCraft(
                                                        craft
                                                    )
                                                }
                                            >

                                                Delete

                                            </button>

                                        </div>

                                    )
                                )}

                            </div>

                        )}

                    </div>

                </div>

            )}


            {/* =====================================================
                DELETE OTP MODAL
            ====================================================== */}

            {showDeleteModal && (

                <div className="modal-overlay">

                    <div className="modal-content otp-modal">

                        <h3>
                            Verify Inward Deletion
                        </h3>


                        <p>
                            A 6-digit verification code has
                            been sent to:
                        </p>


                        <p className="otp-email">

                            {
                                otpRecipientEmail ||
                                "the vendor's registered email"
                            }

                        </p>


                        <p>
                            Enter the code below to confirm
                            deletion of this inward entry.
                        </p>


                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength="6"
                            value={
                                deleteCode
                            }
                            onChange={(e) =>
                                setDeleteCode(
                                    e.target.value
                                        .replace(
                                            /\D/g,
                                            ""
                                        )
                                        .slice(
                                            0,
                                            6
                                        )
                                )
                            }
                            placeholder="Enter 6-digit code"
                            disabled={
                                deletingInward
                            }
                        />


                        {/* =================================================
                            OTP BUTTONS
                        ================================================= */}

                        <div className="delete-modal-buttons">

                            <button
                                type="button"
                                className="cancel-delete-btn"
                                onClick={() => {

                                    setShowDeleteModal(
                                        false
                                    );

                                    setDeleteCode("");

                                    setDeleteInwardId(
                                        null
                                    );

                                    setOtpRecipientEmail("");

                                }}
                                disabled={
                                    deletingInward
                                }
                            >

                                Cancel

                            </button>


                            <button
                                type="button"
                                className="verify-delete-btn"
                                onClick={
                                    verifyDeleteCode
                                }
                                disabled={
                                    deletingInward ||
                                    deleteCode.length !== 6
                                }
                            >

                                {
                                    deletingInward
                                        ? "Deleting..."
                                        : "Verify & Delete"
                                }

                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
};

export default Inwards;